import React, {
    createContext,
    useContext,
    ReactNode,
    Ref, useState, useEffect, useCallback,
} from "react";
import {useFileSelect} from "./../import/useFileSelect";
import {ImportRow, useImportParser} from "./../import/useImportParser";
import {useUserManagementBrowsing} from "../../../lib/user-management/UserManagementBrowsingContext";
import {useToggle} from "./../import/useToggle";
import { VerificationResponseItem, useVerifyImportDevices } from "./useVerifyImportDevices";

type Stage = "select_file" | "verify" | "verify_results" | "submit"

// Define context types using `type`
type ImportDevicesContextType = {
    currentStage: string
    gotoStage: (stage: Stage) => void

    fileInputRef: Ref<HTMLInputElement>
    selectedFile: File | null
    activateFileSelect: () => void
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    selectedFileRows: number | null
    clear: () => void
    departmentsCreated: string[]

    headers: ImportRow
    rows: ImportRow[]

    submitForVerification: () => Promise<void>
    verificationProgress: number
    totalRows: number
    verifying: boolean
    verificationCompleted: boolean
    verificationResults: VerificationResponseItem[],
    clientID: string | undefined,

    missionConnect: boolean;
    setMissionConnect: (value: boolean) => void

    matchOnDeviceID: boolean,
    setMatchOnDeviceID: (value: boolean) => void

    invalidFile: boolean,

    name: string,
    setName: (value: string) => void
    deleteEmptyFields: boolean,
    setDeleteEmptyFields: (value: boolean) => void
};

// Create context with an empty initial value
const ImportDevicesContext = createContext<ImportDevicesContextType | undefined>(undefined);

// Define provider component
type ImportDevicesContextProviderProps = {
    children: ReactNode;
};

const useCurrentStage = (initialStage: Stage = "select_file") => {
    const [currentStage, setCurrentStage] = useState<Stage>(initialStage);
    return {currentStage, gotoStage: setCurrentStage};
}


export const ImportDevicesContextProvider: React.FC<ImportDevicesContextProviderProps> = ({children}) => {

    const [name, setName] = useState("");
    const {clientID} = useUserManagementBrowsing()
    const {currentStage, gotoStage} = useCurrentStage()
    const {fileInputRef, activateFileSelect, selectedFile, handleFileChange, selectedFileRows, clear: clearFile, invalidFile} = useFileSelect();
    const {headers, rows} = useImportParser(selectedFile, currentStage == "select_file");
    const [sendInvitationEmails, , setSendInvitationEmails] = useToggle(false)
    const [missionConnect, , setMissionConnect] = useToggle(false)
    const [matchOnDeviceID, , setMatchOnDeviceID] = useToggle(false)
    const [deleteEmptyFields, , setDeleteEmptyFields] = useToggle(false)

    const {
        submitForVerification,
        verificationProgress,
        totalRows,
        verifying,
        verificationCompleted,
        verificationResults,
        clear: clearVerification,
        departmentsCreated,
    } = useVerifyImportDevices(headers, rows, clientID, matchOnDeviceID, missionConnect, deleteEmptyFields)



    const clear = useCallback(() => {
        clearFile()
        clearVerification()
    }, [clearFile])

    const value: ImportDevicesContextType = {
        currentStage,
        gotoStage,
        fileInputRef,
        activateFileSelect,
        selectedFile,
        handleFileChange,
        selectedFileRows,
        clear,
        headers,
        rows,
        submitForVerification,
        verificationProgress,
        totalRows,
        verifying,
        verificationCompleted,
        verificationResults,
        clientID,
        matchOnDeviceID,
        setMatchOnDeviceID,
        missionConnect,
        setMissionConnect,
        deleteEmptyFields, 
        setDeleteEmptyFields,
        invalidFile,
        departmentsCreated,
        name,
        setName,
    };

    return (
        <ImportDevicesContext.Provider value={value}>
            {children}
        </ImportDevicesContext.Provider>
    );
};

// Custom hook to access the context
export const useDeviceImportContext = (): ImportDevicesContextType => {
    const context = useContext(ImportDevicesContext);
    if (!context) {
        throw new Error("useDevicesImportContext must be used within an ImportDevicesContextProvider");
    }
    return context;
};


